/**
 * HTTP 请求封装
 * 支持 SM3/SM4 加密
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { sm3, sm4 } from 'sm-crypto-v2'
import * as msgpack from 'msgpack-lite'
import { constData } from './storage'
import type { ServiceRequest, ServiceResponse } from '@/types/request.type'
import { getLocUserInfo } from './storage'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://192.168.2.5:8887'

/** 请求超时时间 */
const REQUEST_TIMEOUT = 15000

/** Axios 实例 */
const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: API_BASE_URL,
  headers: {
    accept: '*/*',
    'Content-Type': 'text/plain;charset=utf-8'
  }
})

/**
 * 获取用户信息（从缓存中读取）
 */
const userInfo = getLocUserInfo() || {}

/**
 * 获取用户 Token - 每次从 localStorage 读取最新的用户信息
 */
function getUserToken(): string {
  try {
    // 优先使用全局 userInfo（应用初始化时加载）
    if (userInfo.token) {
      return userInfo.token
    }

    // 如果全局 userInfo 中没有 token，尝试从 localStorage 读取
    const userInfoStr = localStorage.getItem('accountInfo')
    if (userInfoStr) {
      const data = JSON.parse(userInfoStr)
      return data.token || ''
    }
  } catch (error) {
    console.error('Failed to get user token:', error)
  }
  return ''
}

/**
 * 请求参数处理
 */
const paramsHandler: Record<string, (query: any) => any> = {
  GET: (query: any) => {
    query.params = {
      p: `${sm4.encrypt(JSON.stringify(query.data), constData.sm4Def)}`
    }
    query.key = sm3(JSON.stringify(query))
    return query
  },

  POST: (query: any) => {
    query.params = query.data
    query.data = sm4.encrypt(JSON.stringify(query.data), constData.sm4Def)
    query.key = sm3(JSON.stringify(query))
    return query
  },

  PUT: (query: any) => {
    query.params = query.data
    query.data = sm4.encrypt(JSON.stringify(query.data), constData.sm4Def)
    query.key = sm3(JSON.stringify(query))
    return query
  },

  DELETE: (query: any) => {
    query.params = {
      p: `${sm4.encrypt(JSON.stringify(query.data), constData.sm4Def)}`
    }
    query.key = sm3(JSON.stringify(query))
    return query
  }
}

/**
 * 基础请求方法
 */
export const servicev2 = async (query: ServiceRequest): Promise<ServiceResponse> => {
  let res: ServiceResponse = {
    statusCode: 400,
    data: []
  }

  try {
    const token = getUserToken()
    const header = {
      Token: token,
      'Content-Type': 'text/plain;charset=utf-8',
      accept: '*/*'
    }

    if (query.data) {
      query = paramsHandler[query.method](query)
    }
    // //console.log("----------------", {
    //   url: query.url,
    //   data: query.data,
    //   method: query.method,
    //   headers: header
    // });

    res = await axiosInstance.request({
      ...query,
      responseType: "arraybuffer",
      headers: {
        ufo: userInfo.token,
      },
    });
    // console.log(query.url, query, res);
    // 有加密进行解密
    const data = sm4.decrypt(new Uint8Array(res.data), constData.sm4Def, { output: "array" });
    //// //console.log("解密数据", data);
    // 现在使用 Uint8Array 进行解码
    res.data = msgpack.decode(data);


  } catch (error) {
    console.error('Request error:', error)
  }

  return res
}

/**
 * 基础请求方法
 */
export const service = async (query: ServiceRequest): Promise<ServiceResponse> => {
  let res: ServiceResponse = {
    statusCode: 400,
    data: []
  }

  try {
    const token = getUserToken()
    const header = {
      Token: token,
      'Content-Type': 'text/plain;charset=utf-8',
      accept: '*/*'
    }

    if (query.data) {
      query = paramsHandler[query.method](query)
    }

    res = await axiosInstance.request({
      url: query.url,
      data: query.data,
      method: query.method,
      headers: header
    } as AxiosRequestConfig)

    // 解密响应数据
    res.data = sm4.decrypt(res.data as string, constData.sm4Def)

    //console.log("???????????????????========", res);

    res.data = JSON.parse(res.data)
  } catch (error) {
    console.error('Request error:', error)
  }

  return res
}

/**
 * 带缓存的请求方法
 */
const apiDataCache: Record<string, ServiceResponse> = {}

export const serviceBuffer = async (query: ServiceRequest): Promise<ServiceResponse> => {
  let res: ServiceResponse = {
    statusCode: 200,
    data: {
      err: 1,
      msg: '请求失败'
    }
  }

  try {
    if (query.data) {
      query = paramsHandler[query.method](query)
    }

    // 检查缓存
    if (query.cathce === true && apiDataCache[query.key!]) {
      return apiDataCache[query.key!]
    }

    // 获取 Token
    const token = getUserToken()

    // 发起请求
    res = await axiosInstance.request({
      ...query,
      responseType: 'arraybuffer',
      headers: {
        ufo: token,
        Token: token
      }
    } as AxiosRequestConfig)

    // 解密数据
    const data = sm4.decrypt(new Uint8Array(res.data), constData.sm4Def, { output: 'array' })

    // 使用 msgpack 解码
    res.data = msgpack.decode(data)
  } catch (error) {
    console.error('serviceBuffer error:', error)
  }

  // 缓存结果
  if (query.key) {
    apiDataCache[query.key] = res
  }

  return res
}

/**
 * 导出 axios 实例供直接使用
 */
export default axiosInstance
